import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton,
  Checkbox,
  Box,
  Button,
  Tab,
  Tabs,
  Typography,
  Dialog,
} from "@mui/material";
import {
  faBan,
  faCheck,
  faClock,
  faEdit,
  faEye,
  faList,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ParticipantData } from "../../models/Participant";
import Course from "../../models/Course";
import courseService from "../../services/courseService";
import registrationService from "../../services/registrationService";
import { Registration, RegistrationStatus } from "../../models/Registration";
import ParticipantRenderer from "./ParticipantRenderer";
import useNotification from "../../hooks/useNotification";
import useConfirmDialog from "../../hooks/useConfirmationDialog";
import EditParticipantComponent from "./EditParticipantComponent";

interface CourseRosterProps {
  course: Course;
  onParticipantViewChange: (view: boolean) => void;
}

const CourseRoster: React.FC<CourseRosterProps> = ({
  course,
  onParticipantViewChange,
}) => {
  const [selectedParticipant, setSelectedParticipant] =
    useState<ParticipantData | null>(null);
  const [selectedParticipantRegistration, setSelectedParticipantRegistration] =
    useState<Registration | null>(null);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [
    selectedWaitlistRegistrationsIds,
    setSelectedWaitlistRegistrationsIds,
  ] = useState<string[]>([]);
  const [selectedRegistration, setSelectedRegistration] =
    useState<Registration | null>(null);

  const [editingParticipantId, setEditingParticipantId] = useState<
    string | null
  >(null);

  const { showNotification } = useNotification();
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();

  const handleStatusChange = async (
    registration: Registration,
    newStatus: RegistrationStatus
  ) => {
    try {
      //get confirmation from user using a dialog box from mui
      const confirmation = await confirmDialog({
        title: "Confirm Status Change",
        content: `Are you sure you want to change the status of ${registration.participant?.firstName} ${registration.participant?.lastName} to ${newStatus}?`,
        isDangerous: true,
      });
      if (!confirmation) {
        return;
      }

      const updatedRegistration =
        await registrationService.changeRegistrationStatus(
          course.id.toString(),
          registration.id,
          newStatus
        );
      const updatedRegistrations = registrations.map((reg) =>
        reg.id === registration.id ? updatedRegistration : reg
      );
      setRegistrations(updatedRegistrations);
      // Update your local state with the updatedRegistration
    } catch (error) {
      console.error("Error changing registration status:", error);
      showNotification("Error changing registration status", "error");
    }
  };

  const handleDeleteParticipantRegistration = async (
    registration: Registration,
    courseName: string
  ) => {
    try {
      //get confirmation from user using a dialog box from mui
      const confirmation = await confirmDialog({
        title: "Confirm Registration Delete",
        content: `Are you sure you want to delete the registration of ${registration.participant?.firstName} ${registration.participant?.lastName} for ${courseName}?`,
        isDangerous: true,
      });
      if (!confirmation) {
        return;
      }
      await registrationService.deleteRegistration(course.id, registration.id).then(() => {
        const updatedRegistrations = registrations.filter((reg) =>
          reg.id !== registration.id
        );
        setRegistrations(updatedRegistrations);
      });
    } catch (error) {
      console.error("Error changing registration status:", error);
      showNotification("Error changing registration status", "error");
    }
  };

  const moveWaitlistedToAccepted = async () => {
    try {
      //need to check to see if moving to approved will put the course over max students
      //if it will, keep oldest waitlisted participants on waitlist and move the rest to accepted and notify the user that some participants were not moved
      //if it won't, move all selected waitlisted participants to accepted
      const maxStudents = course.maxStudents;
      const currentAcceptedParticipants = registrations.filter(
        (r) => r.status === RegistrationStatus.APPROVED
      ).length;
      const participantsToMoveCount = selectedWaitlistRegistrationsIds.length;
      let participantsToMove: string[] = [];
      if (currentAcceptedParticipants + participantsToMoveCount > maxStudents) {
        showNotification(
          "Moving selected participants to accepted would exceed the maximum number of students for the course. Some participants will remain on the waitlist.",
          "warning"
        );
        //so we need to move the participants that can be moved to accepted until the course is full, based on registration.waitlistPosition
        //we need to move the oldest waitlisted participants first
        const sortedWaitlistParticipants = filteredRegistrations.sort(
          (a, b) => (a.waitlistPosition || 0) - (b.waitlistPosition || 0)
        );
        participantsToMove = sortedWaitlistParticipants
          .slice(0, participantsToMoveCount - 1)
          .map((p) => p.id);

        // const remainingParticipants = sortedWaitlistParticipants.slice(particiapntsToMoveCount);
        // setSelectedWaitlistRegistrationsIds(remainingParticipants.map((p) => p.id));
      } else {
        participantsToMove = selectedWaitlistRegistrationsIds;
      }

      const confirmation = await confirmDialog({
        title: "Confirm Move to Accepted",
        content: `Are you sure you want to move the selected participants to accepted?`,
        isDangerous: true,
      });
      if (!confirmation) {
        return;
      }
      //only move them after confirmation
      setSelectedWaitlistRegistrationsIds(participantsToMove);

      const returnedRegistrations = await registrationService.moveFromWaitlist(
        course.id.toString(),
        participantsToMove,
        RegistrationStatus.APPROVED
      );
      //refetch registrations, is there a way with useEffect to refetch registrations when the course is updated?
      const fetchedRegistrations =
        await registrationService.getRegistrationsForCourse(
          course.id.toString(),
          true
        );
      setRegistrations(fetchedRegistrations);

      setSelectedWaitlistRegistrationsIds([]);
    } catch (error) {
      console.error("Error moving waitlisted participants:", error);
      showNotification("Error moving waitlisted participants", "error");
      // Handle the error
    }
  };
  const fetchRegistrations = async () => {
    const fetchedRegistrations =
      await registrationService.getRegistrationsForCourse(
        course.id.toString(),
        true
      );
    setRegistrations(fetchedRegistrations);
  };

  useEffect(() => {
    fetchRegistrations().catch(console.error);
  }, [course.id]);

  useEffect(() => {
    if (selectedParticipant && course) {
      const fetchSelectedParticipantRegistration = async () => {
        const registration =
          await registrationService.getRegistrationDataForCourseAndParticipant(
            course.id.toString(),
            selectedParticipant?.id
          );
        setSelectedParticipantRegistration(registration);
      };

      fetchSelectedParticipantRegistration().catch(console.error);
    } else {
      setSelectedParticipantRegistration(null);
    }
  }, [selectedParticipant, course]);

  const handleViewUserDetails = (participant: ParticipantData) => {
    setSelectedParticipant(participant);
    onParticipantViewChange(true);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleWaitlistCheckboxChange = (registrationId: string) => {
    setSelectedWaitlistRegistrationsIds((prev) =>
      prev.includes(registrationId)
        ? prev.filter((id) => id !== registrationId)
        : [...prev, registrationId]
    );
  };
  const handleParticipantViewReturn = async (updateCourseRoster: boolean) => {
    if (updateCourseRoster) {
      const fetchedRegistrations =
        await registrationService.getRegistrationsForCourse(
          course.id.toString(),
          true
        );
      setRegistrations(fetchedRegistrations);
    }
    setSelectedParticipant(null);
  };

  const handleEditParticipant = (participantId: string | undefined) => {
    if (participantId) {
      setEditingParticipantId(participantId);
    }
  };

  const handleEditParticipantClose = (success: boolean) => {
    if (success) {
      fetchRegistrations().catch(console.error);
    }
    setEditingParticipantId(null);
  };

  const filteredRegistrations = registrations
    .filter((registration) => {
      switch (tabValue) {
        case 0:
          return registration?.status === RegistrationStatus.APPROVED;
        case 1:
          return registration?.status === RegistrationStatus.PENDING;
        case 2:
          return registration?.status === RegistrationStatus.WAITLIST;
        case 3:
          return registration?.status === RegistrationStatus.DENIED;
        default:
          return false;
      }
    })
    .slice() // avoid mutating original array
    .sort((a, b) => {
      const lastA = a.participant?.lastName?.toLowerCase() || "";
      const lastB = b.participant?.lastName?.toLowerCase() || "";
      if (lastA < lastB) return -1;
      if (lastA > lastB) return 1;
      const firstA = a.participant?.firstName?.toLowerCase() || "";
      const firstB = b.participant?.firstName?.toLowerCase() || "";
      if (firstA < firstB) return -1;
      if (firstA > firstB) return 1;
      return 0;
    });

  const sortedWaitlistParticipants = filteredRegistrations.sort(
    (a, b) => (a.waitlistPosition || 0) - (b.waitlistPosition || 0)
  );

  const ActionButtons = ({ registration }: { registration: Registration }) => {
    switch (registration.status) {
      case RegistrationStatus.APPROVED:
        return (
          <>
            <Tooltip title="Move to Pending">
              <IconButton
                onClick={() =>
                  handleStatusChange(registration, RegistrationStatus.PENDING)
                }
                size="small"
              >
                <FontAwesomeIcon icon={faClock} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Move to Denied">
              <IconButton
                onClick={() =>
                  handleStatusChange(registration, RegistrationStatus.DENIED)
                }
                size="small"
              >
                <FontAwesomeIcon icon={faBan} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Participant">
              <IconButton
                onClick={() =>
                  handleEditParticipant(registration.participant?.id)
                }
                size="small"
              >
                <FontAwesomeIcon icon={faEdit} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Participant">
              <IconButton
                onClick={() =>
                  handleDeleteParticipantRegistration(registration, course.name)
                }
                size="small"
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </Tooltip>
          </>
        );
      case RegistrationStatus.PENDING:
        return (
          <>
            <Tooltip title="Approve">
              <IconButton
                onClick={() =>
                  handleStatusChange(registration, RegistrationStatus.APPROVED)
                }
                size="small"
              >
                <FontAwesomeIcon icon={faCheck} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Move to Waitlist">
              <IconButton
                onClick={() =>
                  handleStatusChange(registration, RegistrationStatus.WAITLIST)
                }
                size="small"
              >
                <FontAwesomeIcon icon={faList} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Deny">
              <IconButton
                onClick={() =>
                  handleStatusChange(registration, RegistrationStatus.DENIED)
                }
                size="small"
              >
                <FontAwesomeIcon icon={faBan} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Participant">
              <IconButton
                onClick={() =>
                  handleDeleteParticipantRegistration(registration, course.name)
                }
                size="small"
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </Tooltip>
          </>
        );
      case RegistrationStatus.WAITLIST:
        return (
          <>
            <Tooltip title="Approve">
              <IconButton
                onClick={() =>
                  handleStatusChange(registration, RegistrationStatus.APPROVED)
                }
                size="small"
              >
                <FontAwesomeIcon icon={faCheck} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Move to Denied">
              <IconButton
                onClick={() =>
                  handleStatusChange(registration, RegistrationStatus.DENIED)
                }
                size="small"
              >
                <FontAwesomeIcon icon={faBan} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Participant">
              <IconButton
                onClick={() =>
                  handleDeleteParticipantRegistration(registration, course.name)
                }
                size="small"
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </Tooltip>
          </>
        );
      case RegistrationStatus.DENIED:
        return (
          <>
            <Tooltip title="Move to Waitlist">
              <IconButton
                onClick={() =>
                  handleStatusChange(registration, RegistrationStatus.WAITLIST)
                }
                size="small"
              >
                <FontAwesomeIcon icon={faList} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Move to Pending">
              <IconButton
                onClick={() =>
                  handleStatusChange(registration, RegistrationStatus.PENDING)
                }
                size="small"
              >
                <FontAwesomeIcon icon={faClock} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Participant">
              <IconButton
                onClick={() =>
                  handleDeleteParticipantRegistration(registration, course.name)
                }
                size="small"
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </Tooltip>
          </>
        );
      default:
        return null;
    }
  };

  const RegistrationList = ({
    registrations,
  }: {
    registrations: Registration[];
  }) => (
    <TableContainer
      component={Paper}
      sx={{
        flexGrow: 1,
        overflow: "auto",
        maxHeight: "calc(100vh - 250px)",
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {tabValue === 2 && (
              <TableCell padding="checkbox" sx={{ width: 50 }}></TableCell>
            )}
            <TableCell sx={{ width: 200 }}>Name</TableCell>
            <TableCell sx={{ width: 200 }}>Email</TableCell>
            <TableCell sx={{ width: 200 }}>Organization</TableCell>
            {tabValue === 2 && (
              <TableCell sx={{ width: 200 }}>Waitlist Position</TableCell>
            )}
            <TableCell sx={{ width: 50 }}>View</TableCell>
            <TableCell sx={{ width: 200 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {registrations.map((registration) => (
            <TableRow key={registration.id}>
              {tabValue === 2 && (
                <TableCell padding="checkbox" align="left">
                  <Checkbox
                    checked={selectedWaitlistRegistrationsIds.includes(
                      registration.id || ""
                    )}
                    onChange={() =>
                      handleWaitlistCheckboxChange(registration.id || "")
                    }
                  />
                </TableCell>
              )}
              <TableCell align="left">{`${registration.participant?.firstName} ${registration.participant?.lastName}`}</TableCell>
              <TableCell align="left">
                {registration.participant?.email}
              </TableCell>
              <TableCell align="left">
                {registration.participant?.organization}
              </TableCell>
              {tabValue === 2 && (
                <TableCell align="left">
                  {registration.waitlistPosition}
                </TableCell>
              )}
              <TableCell align="left">
                <Tooltip title="View Details">
                  {registration.participant ? (
                    <IconButton
                      onClick={() =>
                        handleViewUserDetails(registration.participant!)
                      }
                      size="small"
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </IconButton>
                  ) : (
                    <IconButton size="small" disabled>
                      <FontAwesomeIcon icon={faEye} />
                    </IconButton>
                  )}
                </Tooltip>
              </TableCell>
              <TableCell align="left">
                <ActionButtons registration={registration} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (selectedParticipant) {
    return (
      <ParticipantRenderer
        participant={selectedParticipant}
        registration={selectedParticipantRegistration}
        course={course}
        onReturn={handleParticipantViewReturn}
      />
    );
  }

  const renderCourseTabs = () => {
    return (
      <>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Accepted" />
          <Tab label="Pending" />
          <Tab label="Waitlisted" />
          <Tab label="Denied" />
        </Tabs>
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            mt: 2,
          }}
        >
          {tabValue === 2 && (
            <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={moveWaitlistedToAccepted}
                disabled={selectedWaitlistRegistrationsIds.length === 0}
              >
                Move Selected to Accepted
              </Button>
            </Box>
          )}
          <RegistrationList
            registrations={
              tabValue === 2
                ? sortedWaitlistParticipants
                : filteredRegistrations
            }
          />
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ flexShrink: 0, pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="h6" component="div">
            {course.name}
          </Typography>
          <Typography variant="body2" component="div">
            Approved Students:{" "}
            {
              registrations.filter(
                (r) => r.status === RegistrationStatus.APPROVED
              ).length
            }
            /{course.maxStudents}
          </Typography>
        </Box>
        {/* <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Accepted" />
          <Tab label="Pending" />
          <Tab label="Waitlisted" />
          <Tab label="Denied" />
        </Tabs> */}
      </Box>
      {editingParticipantId ? (
        <EditParticipantComponent
          participantId={editingParticipantId}
          onClose={handleEditParticipantClose}
        />
      ) : (
        renderCourseTabs()
      )}
      {ConfirmDialogComponent}
    </Box>
  );
};

export default CourseRoster;
